import { forkJoin, Observable, of as observableOf, Subscription } from 'rxjs';
import { catchError, flatMap, map, tap } from 'rxjs/operators';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { Location } from '@angular/common';
import { HotelService, ISelectedQuotation } from 'libraries/hotel/services/hotel.service';
import { QuotationService } from 'libraries/hotel/services/quotation/quotation.service';
import { HotelsListService } from './services/hotels-list.service';
import { ViewService } from 'libraries/hotel/services/view.service';
import { MESSAGES } from 'libraries/hotel/messages';
import { IOtherRoomsRequest, IQuoteRequest } from 'libraries/service/resources/hotel/ISearchParameters';
import { IAlternative, IHotel, IHotelConfig } from 'libraries/hotel/models/hotel';
import { IMenuDetails } from 'libraries/hotel/models/menu-details';
import { IDataDetailsDescription, IDataDetailsOffer, IDetails } from 'libraries/hotel/models/details';
import { IView } from 'libraries/hotel/models/view';
import { MENU_DETAILS_ITEMS, NOTIFICATIONS } from 'libraries/hotel/initializer';
import { giata, WORDING } from './config';
import { IqbhMapService } from './services/iqbh-map.service';
import {
    HotelInformationsModalComponent
} from 'libraries/hotel/components/hotel-informations-modal/hotel-informations-modal.component';
import { HotelResourceService } from 'libraries/service/resources/hotel/hotelResource.service';
import {
    ICurrencies,
    IGetPropertyResponse,
    IHotelQuoteResponse,
    ISearchOffersResponse
} from 'libraries/service/resources/hotel/ISearchResponse';
import { INotification, NotificationType } from 'libraries/interfaces/INotification';
import { IConfigSharedHotel } from '../../models/config-shared-hotel';
import { HotelCurrencyService } from 'libraries/service/hotel-currency/hotel-currency.service';
import { IHotelQuoteResourceResponse } from "libraries/service/resources/hotel/hotel-quotation";
import { Yii } from "libraries/interfaces/Yii";
import { IChangeRoom } from './services/IChangeRoom';
import { ICurrency } from 'libraries/service/resources/hotel/IResourceHotel';
import { IOverlay } from './services/IOverlay';
import { RuntimeError } from "libraries/service/error-tracing/runtime-error.model";
import { ErrorTracingService } from "libraries/service/error-tracing/error-tracing.service";
import { ILabels } from "../../../interfaces/ILabels";
import { ConfigSharedHotelService } from 'libraries/service/resources/configuration/config-shared-hotel.service';
import { PropertyResourceService } from "libraries/service/resources/hotel/property-resource.service";
import {EStayPermissions} from "../../../enums/e-stay-permissions";
import { UrlHelperService } from "libraries/service/url-helper/url-helper.service";
import { PermissionResourceService } from "libraries/service/resources/permission/permissionResource.service";

export interface IGetHotelQuote {
    request: IQuoteRequest;
    hotelInformations: IHotel;
}

@Component({
    selector: 'hotels-list',
    templateUrl: './hotels-list.component.html',
    styleUrls: ['./hotels-list.component.scss', '../../styles/hotel-modal.component.scss'],
})
export class HotelsListComponent implements OnInit, OnDestroy {

    @Input()
    public isBatchContext: boolean = false;
    @Input()
    public hotelConfig: IHotelConfig;
    @Input()
    public offerConfig: IHotelConfig;
    @Input()
    public currencies: ICurrencies;
    @Input()
    public configSharedHotel: IConfigSharedHotel;
    @Input()
    public selectedHotelKey?: string;

    @Output()
    public readonly onSelectHotel: EventEmitter<IHotelQuoteResourceResponse> = new EventEmitter();
    @Output()
    public readonly onInsert: EventEmitter<IGetHotelQuote> = new EventEmitter<IGetHotelQuote>();
    @Output()
    public readonly onInsertError: EventEmitter<Error> = new EventEmitter<Error>();
    @Output()
    public readonly onInsertBegin: EventEmitter<void> = new EventEmitter<void>();

    @ViewChild('detailsModal')
    public detailsModal: HotelInformationsModalComponent;


    public readonly yii: ILabels = WORDING;

    public overlays: IOverlay[] = [];
    public modalError: INotification = NOTIFICATIONS;
    public error: INotification = NOTIFICATIONS;
    public menuDetailsItems: IMenuDetails[] = MENU_DETAILS_ITEMS;
    public selectedMenuDetailsItem: string = 'offers';
    public travellers;
    public selectedQuotation: {
        selected: string;
        hotels: {
            [key: string]: any;
        };
    } = null;
    public hasNotFoundData: boolean = false;
    public currencyCode: string;
    public preferGiataProperty: boolean = false;
    public isPermModalAlternative: boolean = true;
    public searchResultAlternatives: IAlternative[];

    public view: IView;

    public permissions: typeof EStayPermissions = EStayPermissions;

    private hotelsModel: IHotel[];
    private readonly subscription: Subscription = new Subscription();

    constructor(
        private readonly hotelService: HotelService,
        private readonly hotelsListService: HotelsListService,
        private readonly quotationService: QuotationService,
        private readonly viewService: ViewService,
        private readonly iqbhMapService: IqbhMapService,
        private readonly router: Router,
        private readonly location: Location,
        private readonly hotelResource: HotelResourceService,
        private readonly hotelCurrencyService: HotelCurrencyService,
        private readonly errorTracingService: ErrorTracingService,
        private readonly configSharedHotelService: ConfigSharedHotelService,
        private readonly propertyResourceService: PropertyResourceService,
        private readonly urlHelperService: UrlHelperService,
        private readonly permissionResourceService: PermissionResourceService
    ) {
    }

    public get hotels(): IHotel[] {
        return this.hotelsModel;
    }

    @Input()
    public set hotels(values: IHotel[]) {
        this.error.active = false;
        this.hotelsModel = values;
        this.selectedQuotation = null;
        // Prepare overlays for map component
        this.overlays = this.iqbhMapService.prepareOverlays(values);
    }

    public property(): void {
        return this.selectedQuotation.hotels[this.selectedQuotation.selected];
    }

    public ngOnInit(): void {
        this.getConfig();

        this.subscription.add(this.hotelService.getSelectedQuotation()
            .subscribe((quotation: ISelectedQuotation) => {
                if (quotation && quotation.selected && quotation.hotels && quotation.hotels.hasOwnProperty(quotation.selected)) {
                    const property = quotation.hotels[quotation.selected].property;
                    this.currencyCode = this.hotelCurrencyService.getCurrencyString(
                        property.total.cost,
                        this.configSharedHotel
                    );
                }

                this.selectedQuotation = quotation;
            }));

        this.subscription.add(this.viewService.getView()
            .subscribe((view) => this.view = view));
    }

    public ngOnDestroy(): void {
        if (this.subscription) {
            this.subscription.unsubscribe();
        }
        this.closeDetailsModal();
    }

    /**
     * API Functions
     */

    // #TODO 02-05-2018_FG Add this function to HotelsListService
    public getHotelProperty(hotel: IHotel) {
        if (this.selectedQuotation && this.selectedQuotation.hotels && this.selectedQuotation.hotels.hasOwnProperty(hotel.property)) {

            return {
                ...this.selectedQuotation.hotels[hotel.property].property,
                hostRoomNameArray: hotel.hostRoomNameArray,
            };
        }

        return hotel;
    }

    public getHotelCurrency(hotel: IHotel): ICurrency {
        const currency: string = this.hotelCurrencyService.getCurrencyString(
            hotel.total.cost,
            this.configSharedHotel
        );

        return this.currencies[currency];
    }

    /**
     * Get quotes for specific hotel. From this function we get the default selected rooms
     * or the rooms already selected from cache.
     * If hotel already exist then take the one from cache
     * Else get it from DB
     */
    public getHotelQuote({request, hotelInformations}: IGetHotelQuote, openModal: boolean = true): void {
        if (openModal) {
            this.modalError.active = false;
        }

        this.hotelService.setSelectedQuotation({
            ...this.selectedQuotation,
        });

        this.viewService.changeView({
            ...this.view,
            loaders: {
                ...this.view.loaders,
                getHostRooms: true
            }
        });

        this.travellers = this.hotelService.getTravellersDistribution();

        const hotelExist: boolean = this.selectedQuotation
            && this.selectedQuotation.hotels && this.selectedQuotation.hotels.hasOwnProperty(hotelInformations.syscode);

        // https://projets2.afidium.com/issues/33879
        // removed because it broke re-opening a modal on an already opend offer
        // if (hotelExist) {
        //     this.hotelsListService
        //         .updateExistingSelectedHotelToSelectedQuotation(this.selectedQuotation, hotelInformations);
        //
        //     this.menuDetailsItems = this.toggleExistToMenu(
        //         !!this.selectedQuotation.hotels[hotelInformations.syscode].description
        //     );
        //
        //     this.viewService.changeView({
        //         ...this.view,
        //         loaders: {
        //             ...this.view.loaders,
        //             getHostRooms: false
        //         }
        //     });
        // }
            // If hotel doesn't exist in the cache, we get its quotes and the description (getProperty)
            this.getProperties({request, hotelInformations})
                .subscribe({
                    next: (): void => {
                    },
                    error: (): void => {
                    },
                });

        if (openModal) {
            this.detailsModal.show();
        }
    }

    public insertQuote({request, hotelInformations}: IGetHotelQuote): void {

        this.onInsertBegin.emit();

        const hotelExist: boolean = this.selectedQuotation
            && this.selectedQuotation.hotels && this.selectedQuotation.hotels.hasOwnProperty(hotelInformations.syscode);

        if (hotelExist) {
            this.hotelsListService
                .updateExistingSelectedHotelToSelectedQuotation(this.selectedQuotation, hotelInformations);

            this.menuDetailsItems = this.toggleExistToMenu(
                !!this.selectedQuotation.hotels[hotelInformations.syscode].description
            );

        } else {
            // If hotel doesn't exist in the cache, we get its quotes and the description (getProperty)
            this.getProperties({request, hotelInformations})
                .subscribe({
                    next: (response: IGetHotelQuote): void => {
                        this.onInsert.emit(response);
                    },
                    error: (e: Error): void => {
                        console.error(e);
                        this.onInsertError.emit(e);
                    },
                });
        }

    }

    /**
     * Get other from specific host
     * If rooms already exists then take the ones in the cache
     * Else get it from DB
     */
    public getOtherRooms(request: IOtherRoomsRequest): void {
        const property = this.selectedQuotation.selected;
        const currentQuotation = this.selectedQuotation.hotels[property];

        // #TODO 02-05-2018_FG
        // Créer des handler functions pour MAJ correctement les alternives et plus généralement, faciliter
        // Les MAJ des objets globaux
        currentQuotation.property.alternative = this.hotelsListService.refreshAlternatives(currentQuotation, request);

        this.hotelService.setSelectedQuotation({
            ...this.selectedQuotation
        });

        this.viewService.changeView({
            ...this.view,
            loaders: {
                ...this.view.loaders,
                getOtherRooms: true
            }
        });

        const roomsExist = currentQuotation.hasOwnProperty('rooms')
            && currentQuotation.rooms.hasOwnProperty(request.hosts);

        if (roomsExist) {
            this.hotelsListService.updateExistingOhterRooms(this.selectedQuotation, currentQuotation, request);

            this.viewService.changeView({
                ...this.view,
                loaders: {
                    ...this.view.loaders,
                    getOtherRooms: false
                }
            });
        } else {
            this.hotelResource.getRoomsByHost(request).pipe(
                tap(() => this.error.active = false),
                catchError((error: Error) => {
                    this.error = {
                        type: NotificationType.Error,
                        active: true,
                        message: MESSAGES.getRoomsError + error
                    };

                    this.detailsModal.hide();

                    return observableOf(null);
                }))
                .subscribe(
                    (otherRooms: ISearchOffersResponse) => {
                        this.hotelsListService
                            .setOtherRoomsToSelectedQuotation(
                                this.selectedQuotation,
                                currentQuotation,
                                property,
                                otherRooms,
                                request
                            );

                        this.viewService.changeView({
                            ...this.view,
                            loaders: {
                                ...this.view.loaders,
                                getOtherRooms: false
                            }
                        });
                    }
                );
        }
    }

    /**
     * Create quotation then go to the next page if success
     * On fail stay on the search page and display the error message
     */
    public getQuotation(output: IGetHotelQuote): void {
        const request: IQuoteRequest = output.request;
        const hotelInformations: IHotel = output.hotelInformations;
        this.error.active = false;

        this.viewService.changeView({
            ...this.view,
            loaders: {
                ...this.view.loaders,
                getQuotation: true
            }
        });

        const hotelExist: boolean = this.selectedQuotation
            && hotelInformations
            && this.selectedQuotation.hotels
            && (
                this.selectedQuotation.hotels.hasOwnProperty(hotelInformations.syscode)
                || this.selectedQuotation.hotels.hasOwnProperty(request.properties)
            );

        // @ts-ignore
        if (!request.trips && hotelInformations.property && (typeof hotelInformations.property.trip === 'string')) {
            // @ts-ignore
            request.trips = hotelInformations.property.trip;
        }

        if (hotelExist) {
            this.chooseQuote({request, hotelInformations: this.selectedQuotation.hotels[request.properties]});
        } else {
            this.getProperties({request, hotelInformations})
                .subscribe(() => {
                    this.chooseQuote({request, hotelInformations});
                });
        }
    }

    /**
     * Helper Functions
     */
    public changeRoom(room: IChangeRoom): void {
        this.hotelsListService.changeRoom(this.selectedQuotation, room);
    }

    /**
     * UI Functions
     */

    public toggleListMap(): void {
        this.viewService.changeView({
            ...this.view,
            list: !this.view.list,
            map: !this.view.map
        });

        const view: string = this.view.list ? 'list' : 'map';

        // Remove optionals parameters
        const route = this.location.path().split('?');

        // Then set parameters
        this.location.go(route[0], `view=${view}`);
    }

    // #TODO 02-05-2018_FG Rename the function
    public isModified(hotel) {
        if (this.selectedQuotation && this.selectedQuotation.hotels && this.selectedQuotation.hotels.hasOwnProperty(hotel.property)) {
            return true;
        }

        return false;
    }

    public closeDetailsModal() {
        this.detailsModal.hide();
    }

    private getConfig(): void {
        this.configSharedHotelService.getConfiguration().subscribe({
            next: (sharedHotelConfig: IConfigSharedHotel): void => {
                this.preferGiataProperty = sharedHotelConfig.detail.property.preferGiataProperty;
                this.isPermModalAlternative = sharedHotelConfig.detail.property.isPermModalAlternative;
            },
            error: (error: Error): void => {
                this.errorTracingService.captureException(error);
            }
        });
    }

    private toggleExistToMenu(exist: boolean): IMenuDetails[] {
        const newMenuDetailsItems = this.menuDetailsItems.map((item: IMenuDetails): IMenuDetails => {
            if (item.code === 'description' || item.code === 'informations') {
                item.selected = false;
                item.exist = exist;
            } else if (item.code === 'offers') {
                item.selected = true;
                item.exist = true;
            }

            return item;
        });

        return newMenuDetailsItems;
    }

    private hasHostInInformations(hostCode: string, hotelInformations: IHotel): boolean {
        return Boolean(hotelInformations.alternatives) &&
            Boolean(hotelInformations.alternatives.length) &&
            Boolean(hotelInformations.alternatives.find((alternative: IAlternative) => alternative.hostCode === hostCode));
    }

    private getProperties({request, hotelInformations}: IGetHotelQuote): Observable<any> {
        // force giata host if required (AND present !!)
        const propertyHost: string = (this.preferGiataProperty && this.hasHostInInformations(giata, hotelInformations)) ?
            giata :
            request.hosts;

        this.searchResultAlternatives = hotelInformations.alternatives ? [...hotelInformations.alternatives] : [];
        this.error.active = false;
        return this.hotelResource.getQuotes(request)
            .pipe(
                catchError((error: Error) => {
                    this.errorTracingService.captureException(error);
                    this.error = {
                        type: NotificationType.Error,
                        active: true,
                        message: MESSAGES.getHotelQuoteError + error
                    };
                    this.detailsModal.hide();
                    return observableOf(null);
                }),
                flatMap((hotelQuoteResponse: IHotelQuoteResponse) => forkJoin([
                        observableOf(hotelQuoteResponse),
                        // this.propertyResourceService.getHostProperty(
                        //     propertyHost,
                        //     this.getSyscode(hotelInformations, propertyHost),
                        // )
                        // .pipe(
                        //     catchError((error: Error) => {
                        //         this.modalError = {
                        //             type: NotificationType.Warning,
                        //             active: true,
                        //             message: Yii.t('wui-iqbh-js', "Aucune information de l'hôtel n'a été trouvée"),
                        //         };
                        //         return observableOf(null);
                        //     })
                        // )
                    ])
                ),
                map((responses: [
                    IHotelQuoteResponse,
                    // IGetPropertyResponse
                ]) => responses ? this.hotelsListService.setResponseToDetails(responses) : null),
                tap((details: IDetails) => {
                    if (!details) {
                        return;
                    }

                    this.hotelsListService.addSelectedHotelToSelectedQuotation(
                        this.selectedQuotation,
                        hotelInformations,
                        details,
                        propertyHost,
                    );

                    this.menuDetailsItems = this.toggleExistToMenu(Boolean(details && details.description));

                    this.viewService.changeView({
                        ...this.view,
                        loaders: {
                            ...this.view.loaders,
                            getHostRooms: false
                        }
                    });
                })
            );
    }

    private getSyscode(hotelInformations: IHotel, propertyHost: string): string {
        const alternative: IAlternative = hotelInformations.alternatives ?
            hotelInformations.alternatives.find((alternativeItem: IAlternative) => alternativeItem.hostCode === propertyHost) :
            null;

        return (alternative || hotelInformations).syscode;
    }

    private chooseQuote({request, hotelInformations}: IGetHotelQuote): void {

        forkJoin([
            this.hotelsListService.getQuotation(this.selectedQuotation, request),
            this.permissionResourceService.hasPermission(EStayPermissions.SHARED_HOST_SHOW),
        ])
            .subscribe(([quotation, canShowHost]: [IHotelQuoteResourceResponse, boolean]) => {
                    if (!quotation) {
                        this.error = {
                            active: true,
                            type: NotificationType.Error,
                            message: Yii.t('wui-shared-js', 'Aucune offre disponible')
                        };

                        this.viewService.changeView({
                            ...this.view,
                            loaders: {
                                ...this.view.loaders,
                                getQuotation: false
                            }
                        });
                    } else if (quotation) {
                        this.onSelectHotel.emit(quotation);
                        this.closeDetailsModal();
                        this.quotationService.setQuotation(quotation);
                        const key: string = canShowHost ? quotation.key : this.urlHelperService.encodeUrlPart(quotation.key);
                        this.router.navigate(
                            ['/iaqobohotel', 'quotation', key],
                            {
                                queryParams: {
                                    view: this.view.list ? 'list' : 'map'
                                }
                            }
                        );

                        this.viewService.changeView({
                            ...this.view,
                            loaders: {
                                ...this.view.loaders,
                                getQuotation: false
                            }
                        });
                    }
                },
                (error: Error) => {
                    this.errorTracingService.captureException(new RuntimeError("Get quotation error", error));
                    this.error.active = true;
                    this.error.message = `${Yii.t("wui-iqbh-js", "Erreur lors de la cotation : ")} ${error.message}`;

                    this.viewService.changeView({
                        ...this.view,
                        loaders: {
                            ...this.view.loaders,
                            getQuotation: false
                        }
                    });
                });
    }
}
